import { ProductTileProps } from '@pb/components'
import { currentBrand, getLabelFromStorage } from '@pb/utils'
import { useDecision } from '@optimizely/react-sdk'
import { useDispatch, useSelector } from 'react-redux'

import appSelectors from 'App/duck/selectors'
import OPTIMIZELY from 'constants/optimizely' // TODO: BTBPSDL-200 remove OPTIMIZELY once AB test will complete
import { getMiniCartData } from 'features/favourites/duck/normalize'
import {
  operationMoveToCart,
  operationFetchStoreSearchResults,
  operationDeleteFavouritesItem,
  operationUpdateVariant,
  operationResetStoreSearchResults,
} from 'features/favourites/duck/operations'
import { getStoreSearchResultsItems } from 'features/favourites/duck/selectors'
import {
  AddToBagVerificationData,
  NormalizedFavourites,
  StoreSearchQuery,
  TitleTag,
} from 'features/favourites/duck/types'
import {
  trackCloseFindInStore,
  findInStoreToggleSwitch,
  trackSearchLocationFindInStore,
  trackSearchResultFindInStore,
  trackClickOnStoreFindInStore,
  trackPhonenumberFindInStore,
  trackStoreInformationFindInStore,
  trackUserAllowLocationFindInStore,
  trackUserBlockLocationFindInStore,
  trackProductStoreLocatorClick,
  trackProductStoreLocatorWithSizeClick,
} from 'features/favourites/tracking'
import { trackOptimizelyEvent } from 'helpers/optimizely'
import { ThunkResult, RootState } from 'redux/types'

import { extractSizeGuideDrawerData } from '../components/FavouritesProductGrid/helpers'
import {
  CLOSED_FIND_IN_STORE,
  NO_STORE_FOUND_ARTICLE,
  NO_STORE_FOUND_SIZE,
  SUCCESSFULL_SEARCH,
  STORE_INFO_PAGE,
  STORE_DISPLAY,
  STORE_MAP_PAGE_SHOWN,
  STORE_TOGGLE_OFF_LABEL,
  STORE_TOGGLE_ON_LABEL,
  ABANDON_NO_STORE_FOUND,
} from '../constant'
import {
  miniCartLabels,
  generalLabels,
  energyLabelLabels,
  storeSearchLabels,
  sizePickerLabels,
  sizeGuideLabels,
} from '../labels'
import { getRadiusValue } from './getRadiusValue'
import { getSizePickerFlags } from './getSizePickerFlags'

export const useMapFavouritesItems = (
  data: NormalizedFavourites[] | [],
  className?: string,
): ProductTileProps[] | [] => {
  const dispatch = useDispatch()
  const moveToCart = (
    variantCode: string,
    productCode: string,
    addToBagVerificationData: AddToBagVerificationData,
  ): ThunkResult<void> =>
    dispatch(operationMoveToCart(variantCode, productCode, addToBagVerificationData))

  const getStoreData = (searchQuery: StoreSearchQuery): ThunkResult<void> =>
    dispatch(operationFetchStoreSearchResults(searchQuery))

  const deleteFavouriteItem = (productCode: string): ThunkResult<void> =>
    dispatch(operationDeleteFavouritesItem(productCode))

  const updateVariant = (variantCode: string, productCode: string): ThunkResult<void> =>
    dispatch(operationUpdateVariant(variantCode, productCode))

  const resetStoreSearchResults = (): ThunkResult<void> =>
    dispatch(operationResetStoreSearchResults())

  const isNotifyBackEnabled = useSelector(
    (state: RootState) => appSelectors.getSettings(state).notifyBackEnabled,
  )

  // For brands the settings api is not enabled so the value will read from env variable
  const sizepickerNIB =
    process.env.IS_SETTINGS_API_ENABLED === 'enabled' && process.env.NOTIFY_IF_BACK !== 'pb'
      ? isNotifyBackEnabled
      : process.env.NOTIFY_IF_BACK === 'pb'

  const sizepickerHideOOS = useSelector(
    (state: RootState) => appSelectors.getSettings(state).hideOutOfStockVariants,
  )

  const currentSiteCode = useSelector(
    (state: RootState) => appSelectors.getSettings(state).currentSiteCode,
  )

  const storeSearchResultsItems = useSelector(
    (state: RootState) => getStoreSearchResultsItems(state).stores || [],
  )

  const isSmallScreen = useSelector(appSelectors.getIsSmallScreen)

  // TODO: BTBPSDL-200 remove it once AB test will complete
  const [{ enabled: increasedRadiusEnable, variationKey }] = useDecision(
    OPTIMIZELY.INCREASED_SEARCHED_RADIUS,
  )
  const products = data.map((product: NormalizedFavourites) => {
    const addToBagVerificationData: AddToBagVerificationData = {
      ...getMiniCartData(product),
      ...miniCartLabels,
    }

    const lastThreeNumbersInArticleCode =
      product.articleCode && typeof product.articleCode === 'string'
        ? product.articleCode.substring(product.articleCode.length - 3)
        : ''

    const sizePickerFlags = getSizePickerFlags()

    const favouriteData = {
      ...product,
      ...generalLabels,
      className,
      titleTag: TitleTag.H2,
      removeOnClick: () => deleteFavouriteItem(product.code),
      key: `${product.articleCode}-${product.variantCode}`,
      energyLabelProps: {
        ...product.energyLabelProps,
        ...energyLabelLabels,
        compatible: getLabelFromStorage('favorites.energy.classification.compatible.with', {
          replacements: [product.energyLabelProps?.energyClassInterval as string],
        }),
      },
      storeSearchData: {
        ...storeSearchLabels,
        ...product.storeSearchData,
        onAddressSelectionFinished: ({ lat, lng }) =>
          getStoreData({
            countryCode: currentSiteCode,
            articleCode: product.baseProductCode || '',
            variantCode: lastThreeNumbersInArticleCode,
            lat,
            lng,
            radiusInMeters: increasedRadiusEnable ? getRadiusValue(variationKey) : '10000', // TODO: BTBPSDL-200 set it to 10000 once AB test will complete
            maxNumberOfStores: '100',
            brand: '000',
            channel: '02',
          }),
        onStoreLocatorClosed: () => {
          if (trackOptimizelyEvent) trackOptimizelyEvent('close_find_in_store') // TODO: BTBPSDL-200 remove trackOptimizelyEvent once AB test will complete
          trackCloseFindInStore(CLOSED_FIND_IN_STORE)
          resetStoreSearchResults()
        },
        stores: storeSearchResultsItems,
        findInStoreToggleSwitch,
        onStoreSearchFinishedClick: trackSearchLocationFindInStore,
        onSearchResultFindInStore: trackSearchResultFindInStore,
        onClickOnStoreFindInStore: trackClickOnStoreFindInStore,
        onStoreListClick: trackClickOnStoreFindInStore,
        trackOptimizelyEvent,
        trackPhonenumberFindInStore,
        trackCloseFindInStore,
        trackStoreInformationFindInStore,
        trackUserAllowLocationFindInStore,
        trackUserBlockLocationFindInStore,
        noStoreFoundArticle: NO_STORE_FOUND_ARTICLE,
        noStoreFoundSize: NO_STORE_FOUND_SIZE,
        successfullSearch: SUCCESSFULL_SEARCH,
        storeInfoPage: STORE_INFO_PAGE,
        storeDisplay: STORE_DISPLAY,
        storeMapPageShown: STORE_MAP_PAGE_SHOWN,
        storeToggleOffLabel: STORE_TOGGLE_OFF_LABEL,
        storeToggleOnLabel: STORE_TOGGLE_ON_LABEL,
        closedFindInStore: CLOSED_FIND_IN_STORE,
        abandonNoStoreFound: ABANDON_NO_STORE_FOUND,
      },
      onProductStoreLocatorCTAClick: trackProductStoreLocatorClick,
      onProductStoreLocatorWithSizeClick: trackProductStoreLocatorWithSizeClick,
      sizePickerData: {
        ...product.sizePickerData,
        ...sizePickerLabels,
        ...sizePickerFlags,
        nib: sizepickerNIB,
        hideOOS: sizepickerHideOOS,
        drawerData: extractSizeGuideDrawerData(product.sizePickerData),
        sizeGuideLabels,
        shouldScrollBodyToDropDownOnOpen: !isSmallScreen && currentBrand !== 'monki', // Issue with monki header and !shouldScrollBodyToDropDownOnOpen
        onClickChangeVariant: (variantCode: string) => updateVariant(variantCode, product.code),
        moveToCart: (selectedVariantCode: string) =>
          moveToCart(selectedVariantCode, product.code, addToBagVerificationData),
      },
    }

    return favouriteData
  })

  return products as unknown as NormalizedFavourites[]
}
